/**
 * Отчёт оверлея о собственной геометрии.
 *
 * Зачем. Стример настраивает Browser Source руками и ошибается
 * предсказуемо: ставит источник 1280×720 при сцене 1920×1080,
 * а потом растягивает его мышью до размера кадра. Картинка при этом
 * работает — только текст мыльный, а тонкие линии рвутся. Изнутри
 * страницы такое несовпадение не видно никак: оверлей знает лишь,
 * что ему дали 1280 пикселей.
 *
 * Поэтому оверлей сообщает всё, что о себе знает, а сравнивает
 * панель — там это видит человек, который может пойти и поправить.
 *
 * ЧЕГО ЗДЕСЬ НЕТ И НЕ БУДЕТ: трансформации источника в сцене OBS.
 * Сдвинут ли источник, обрезан ли, растянут ли мышью — изнутри
 * страницы узнать нельзя вовсе, это умеет только obs-websocket,
 * отложенный бессрочно (docs/06). Режим настройки (задача 2.13a)
 * не даёт этого знания, но делает несовпадение видимым.
 */

/** Что оверлей знает о себе. */
export interface Geometry {
  /** Размер страницы в пикселях CSS. */
  readonly width: number;
  readonly height: number;
  /**
   * Отношение физических пикселей к CSS-пикселям.
   *
   * У OBS обычно 1, но на мониторе с масштабированием бывает 1.25
   * или 2 — и тогда «мыльность» объясняется именно этим, а не
   * размером источника.
   */
  readonly dpr: number;
  /** Размер сцены OBS, если он известен. */
  readonly obsScene: { readonly width: number; readonly height: number } | null;
  /** Показывается ли источник прямо сейчас. */
  readonly active: boolean;
}

/** Тот кусочек OBS, который доступен странице в Browser Source. */
interface ObsStudio {
  getCurrentScene?: (callback: (scene: { width?: number; height?: number }) => void) => void;
}

/**
 * Собирает геометрию.
 *
 * `obsstudio` есть только внутри Browser Source; в обычном браузере
 * его нет, и это не ошибка — оверлей должен открываться и там,
 * иначе его нечем проверить.
 */
export function readGeometry(win: Window & { obsstudio?: ObsStudio }, active: boolean): Geometry {
  return {
    width: win.innerWidth,
    height: win.innerHeight,
    dpr: win.devicePixelRatio,
    obsScene: null,
    active,
  };
}

/**
 * Читает из события OBS, показывается ли источник.
 *
 * `null` означает «событие не про это»: чужое, без `detail` или
 * с полем не того вида. Отдельно от `false`, потому что разница
 * существенная — не знать и знать, что источник скрыт, приводят
 * к разным ответам панели.
 *
 * ПОЧЕМУ `obsSourceActiveChanged`, А НЕ `visibilitychange`.
 * Страница в Browser Source остаётся видимой, даже когда источник
 * убран из эфирной сцены: OBS её просто не показывает, а браузер
 * об этом ничего не знает. `visibilitychange` там срабатывает лишь
 * при выключенном источнике с «Shutdown source when not visible» —
 * то есть в одном случае из многих. Полагаться только на него
 * значило бы показывать в панели «оверлей в эфире», когда его там
 * давно нет.
 *
 * `obsSourceVisibleChanged` намеренно не слушается: он про галочку
 * в списке источников, а вопрос панели — видит ли это зритель.
 */
export function activeFromEvent(event: unknown): boolean | null {
  if (typeof event !== 'object' || event === null) return null;

  const detail = (event as { detail?: unknown }).detail;
  if (typeof detail !== 'object' || detail === null) return null;

  const active = (detail as { active?: unknown }).active;

  return typeof active === 'boolean' ? active : null;
}

/**
 * Спрашивает у OBS размер текущей сцены.
 *
 * Обратным вызовом, а не обещанием: таков интерфейс obsstudio.
 * Если его нет или он молчит, возвращается null — панель в этом
 * случае просто не покажет сравнение, а не соврёт.
 */
export function askObsScene(
  win: Window & { obsstudio?: ObsStudio },
  then: (size: { width: number; height: number } | null) => void,
): void {
  const api = win.obsstudio;
  if (api?.getCurrentScene === undefined) {
    then(null);
    return;
  }

  let answered = false;

  try {
    api.getCurrentScene((scene) => {
      if (answered) return;
      answered = true;

      const width = scene.width ?? 0;
      const height = scene.height ?? 0;

      then(width > 0 && height > 0 ? { width, height } : null);
    });
  } catch {
    // Версии OBS отличаются, и метод может отсутствовать или бросать.
    // Оверлей от этого падать не должен: он рисует эфир
    then(null);
  }
}

/** Сообщение о геометрии для отправки в hub. */
export function geometryMessage(geometry: Geometry): string {
  return JSON.stringify({ t: 'geometry', ...geometry });
}
